// eslint-disable-next-line import/no-extraneous-dependencies
import {
  AWS_STAGE_TYPE,
  AwsStageType,
  DEPLOYMENT_TARGET_ROLE,
  DeploymentTargetRoleType,
} from "@codedrifters/utils";
import { Component } from "projen";
import { AwsCdkTypeScriptApp } from "projen/lib/awscdk";
import { BuildWorkflow, BuildWorkflowOptions } from "projen/lib/build";
import { GitHub, GithubWorkflow, WorkflowSteps } from "projen/lib/github";
import { JobPermission, JobStep } from "projen/lib/github/workflows-model";
import { ValueOf } from "type-fest";
import {
  AwsDeploymentConfig,
  CDK_DIFF_METHOD,
  renderCdkDeploy,
  renderCdkDiff,
  renderCdkList,
} from "../aws";
import { ApplyWorkflow } from "./apply-workflow";
import { addBuildCompleteJob } from "./build-complete-job";
import {
  APPROVE_JOB_ID,
  DEPLOY_APPROVALS,
  DEPLOY_GATE,
  DeployApprovals,
  DeployGate,
  resolveApprovalGate,
  resolveDiffEnvironment,
  resolveEnvironmentGate,
} from "./deploy-gate";
import {
  DIFF_NEW_STACK_MARKER,
  DIFF_OUTPUT_DIRECTORY,
  DIFF_PART_ARTIFACT_PREFIX,
  DIFF_REPORT_JOB_ID,
  DiffReportJob,
  renderDiffPartUploadStep,
} from "./diff-report-job";
import {
  WorkflowHomeRepository,
  renderHomeRepositoryCondition,
} from "./home-repository";
import { renderSetupNode, renderSetupPnpm } from "./node-setup";
import {
  BUILD_ARTIFACT_NAME,
  PERMISSION_BACKUP_FILE,
  PLAN_RUN_ID_INPUT,
  PlanApplyOptions,
  VALIDATE_PLAN_JOB_ID,
} from "./plan-apply";
import { AwsDeploymentTarget } from "../aws/aws-deployment-target";
import { GitBranch } from "../git";
import { MonorepoProject } from "../projects";
import { ROOT_CI_TASK_NAME, TurboRepo } from "../turbo";

export const PROD_DEPLOY_NAME = "prod-deploy";

/**
 * The branch a run is *for*, as a GitHub Actions expression.
 *
 * `head_ref` is set on `pull_request` only, so this resolves to the source
 * branch on a PR and to the pushed/dispatched ref everywhere else. Shared by
 * the build job's `GIT_BRANCH_NAME` and by {@link
 * DeployWorkflowOptions.branchScopedConcurrency}: a consumer whose stack names
 * are derived from `GIT_BRANCH_NAME` only gets a correct concurrency key if
 * both read the same branch.
 */
const BRANCH_NAME_EXPRESSION = "${{ github.head_ref || github.ref_name }}";

/**
 * Opt-in `cdk diff` capture for a deploy workflow.
 *
 * With `enabled` unset (the default), no diff jobs are emitted and the
 * generated workflow is byte-identical to one configured without a `diff`
 * block at all.
 */
export interface DeployDiffOptions {
  /**
   * Emit one `cdk diff` job per deployment target.
   *
   * @default false
   */
  readonly enabled?: boolean;

  /**
   * Upload the captured diff as a per-run CI artifact, named uniquely per
   * target so parallel diff jobs do not collide under `actions/upload-artifact`
   * v4+ immutability semantics.
   *
   * @default true (when `enabled`)
   */
  readonly artifact?: boolean;

  /**
   * Render the captured diff into the job summary, wrapped in a `<details>`
   * block and truncated below GitHub's per-step summary limit.
   *
   * @default true (when `enabled`)
   */
  readonly summary?: boolean;

  /**
   * Put the workflow's `environmentName` on each diff job, so it authenticates
   * with the same OIDC subject claim as the deploy jobs it informs.
   *
   * A job's `sub` claim is `repo:<owner>/<repo>:environment:<name>` when the
   * job declares an environment and `repo:<owner>/<repo>:ref:refs/heads/<branch>`
   * when it does not. Under the `environment` gate the diff jobs are the only
   * cloud-touching jobs left on the ref form, so a deployer-role trust policy
   * narrowed to environment subjects alone rejects every one of them at the
   * credentials step — and because the merged report gates the deploys, the run
   * dies before anything deploys.
   *
   * Requires `gate: "environment"` and `enabled`; anything else fails synth.
   *
   * This does **not** gate the diff. An environment with no protection rules
   * adds no prompt, which is what keeps the diff readable before the approval
   * it exists to inform. A required-reviewer rule on the *deploy* environment
   * would make every diff job prompt individually — put that rule on the
   * separate `approvalEnvironmentName` under `approvals: "once"` instead.
   *
   * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
   * @default false
   */
  readonly environment?: boolean;
}

export interface DeployWorkflowOptions {
  /**
   * What type of deploy is this workflow for?
   *
   * @default AWS_STAGE_TYPE.DEV
   */
  readonly awsStageType?: ValueOf<typeof AWS_STAGE_TYPE>;

  /**
   * Optionally feed a list of targets to deploy to.
   *
   * @default discovers all targets using stageType
   */
  readonly awsDeploymentTargets?: Array<AwsDeploymentTarget>;

  /**
   * Key each deploy job's concurrency group on the branch as well as the
   * deployment target.
   *
   * A deploy job's group is otherwise the target coordinates alone
   * (`<stage>-<role>-deploy-<project>-<account>-<region>`), and GitHub scopes
   * concurrency groups to the *repository* — so one group serializes every run
   * of every workflow carrying that job, on every branch. That is what you want
   * when each branch deploys the same stacks.
   *
   * It is not what you want when the CDK app derives its stack names from the
   * branch: two branches then update entirely separate stacks while queueing
   * behind one lock. Setting this appends the branch, so branches deploy in
   * parallel and same-branch runs still serialize.
   *
   * Enabling this lets per-branch deploys run concurrently into the *same* AWS
   * account, so anything they share outside their own stacks — a parent hosted
   * zone, an account-level singleton — has to tolerate concurrent mutation.
   *
   * Applies to the deploy job and — when diff capture is on — to each target's
   * diff job, which locks the same branch's stacks for the same reason. The
   * `plan-apply` gate's apply job keeps the unsuffixed group: an apply is
   * dispatched from whatever ref the operator happens to be on, which says
   * nothing about the branch that was planned, so a branch key there would
   * weaken the mutual exclusion rather than sharpen it.
   *
   * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
   * @default false - the group is the deployment target alone
   */
  readonly branchScopedConcurrency?: boolean;

  /**
   * Existing workflow, useful if we're tacking deployments onto an existing
   * build workflow
   */
  readonly buildWorkflow?: BuildWorkflow;

  /**
   * Options for the build workflow, if no build workflow is provided.
   */
  readonly buildWorkflowOptions?: Partial<BuildWorkflowOptions>;

  /**
   * Projects that should complete deployment before this one starts.
   */
  readonly deployAfterTargets?: Array<AwsDeploymentTarget>;

  /**
   * Opt into a `cdk diff` job per deployment target.
   *
   * The `cdk diff` flags themselves are **not** configured here — they come
   * from the {@link CdkCli} precedence chain (per-stage built-in → per-stage
   * `diffDefaults` → per-account override → per-target `cdkOptions.diff`). This
   * option only controls whether the jobs are emitted, what they publish, and —
   * via {@link DeployDiffOptions.environment} — which OIDC subject claim they
   * authenticate with.
   *
   * @default - no diff jobs are emitted, unless `gate` is `plan-apply`
   */
  readonly diff?: DeployDiffOptions;

  /**
   * Hold this workflow's deploy jobs behind a gate.
   *
   * `"environment"` puts a GitHub environment on every deploy job, so its
   * protection rules apply before the job is sent to a runner. Requires
   * {@link environmentName}.
   *
   * `"plan-apply"` splits the workflow instead: this one becomes a plan that
   * builds and diffs but never deploys, and a second dispatch-only apply
   * workflow deploys the exact assembly a nominated plan run produced. Needs
   * no protection rule, so it works on every GitHub plan.
   *
   * Adding an environment changes a job's OIDC subject claim, so deployer-role
   * trust policies must be updated in the same change.
   *
   * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
   * @default - deploy jobs are ungated and stay in this workflow
   */
  readonly gate?: DeployGate;

  /**
   * Name of the GitHub environment gated jobs are held behind. Required when
   * {@link gate} is `"environment"`, optional when it is `"plan-apply"` — where
   * it layers an environment onto the apply jobs — and rejected when {@link
   * gate} is unset.
   *
   * The environment must be created and configured deliberately — referencing a
   * name that does not exist creates it with no protection rules at all.
   *
   * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
   * @default - no environment is applied
   */
  readonly environmentName?: string;

  /**
   * How many approvals the `"environment"` gate asks for across one run.
   *
   * `"per-target"` (the default) leaves the gate exactly as it was: the
   * environment sits on every deploy job, so each one reaches its protection
   * rules separately. Deploy jobs chained by {@link deployAfterTargets} reach
   * them one at a time, with a full deploy between each prompt, so an operator
   * approves N times with a wait in between.
   *
   * `"once"` emits a single `approve` job behind {@link
   * approvalEnvironmentName} and holds every deploy job behind it, so one
   * approval after the diff report releases the whole run. The deploy jobs keep
   * their own `environment`, so OIDC subject claims, environment-scoped
   * secrets, and per-stack deployment history are unchanged. Requires {@link
   * approvalEnvironmentName} and the `"environment"` gate.
   *
   * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
   * @default DEPLOY_APPROVALS.PER_TARGET - one approval per deploy job
   */
  readonly approvals?: DeployApprovals;

  /**
   * Name of the GitHub environment the single `approve` job is held behind.
   * Required when {@link approvals} is `"once"`, and rejected otherwise.
   *
   * Must differ from {@link environmentName}: this is the environment carrying
   * the required-reviewer rule, while the deploy environment keeps the secrets
   * and the deployment history. An administrator has to create it with that
   * rule and remove the rule from the deploy environment, or the run prompts
   * twice.
   *
   * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
   * @default - no approval job is emitted
   */
  readonly approvalEnvironmentName?: string;

  /**
   * Extra settings for the `plan-apply` gate. Ignored under every other gate.
   *
   * @default - the apply workflow is `<plan-workflow-name>-apply` with no
   * head-drift or digest assertions
   */
  readonly planApply?: PlanApplyOptions;

  /**
   * Pin this workflow's cloud-touching jobs to one repository, as an
   * `owner/repo` slug.
   *
   * Guards every deploy and diff job with
   * `github.repository == '<owner>/<repo>'` so a mirror or clone of the
   * consumer repo hosted elsewhere skips them instead of failing at the OIDC
   * credentials step. The build job is intentionally left unguarded.
   *
   * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
   * @default - no repository guard is emitted
   */
  readonly homeRepository?: string;
}

/**
 * Adds a build + deploy GitHub Actions workflow for an AwsCdkTypeScriptApp.
 *
 * Deploy URLs are surfaced via a naming convention: any CfnOutput whose
 * logical ID ends in `Endpoint` is rendered into the PR sticky comment and
 * the job summary. See the docs page for the full convention and naming
 * guidance.
 *
 * @see `docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow.md`
 */
export class AwsDeployWorkflow extends Component {
  public static of(
    project: AwsCdkTypeScriptApp,
    buildWorkflow: BuildWorkflow,
  ): AwsDeployWorkflow | undefined {
    const isDefined = (c: Component): c is AwsDeployWorkflow =>
      c instanceof AwsDeployWorkflow && c.buildWorkflow === buildWorkflow;
    return project.components.find(isDefined);
  }

  /**
   * The root project for this deploy workflow. Must be a monorepo project.
   */
  private rootProject: MonorepoProject;

  /**
   * What type of deploy is this workflow for?
   */
  public awsStageType: AwsStageType;

  /**
   * AWS environment type, such as primary or secondary.
   *
   * @deprecated Use deployment target role terminology elsewhere. This property is maintained for backward compatibility.
   * @default 'primary' (this is the only type supported currently)
   */
  public awsEnvironmentType: DeploymentTargetRoleType =
    DEPLOYMENT_TARGET_ROLE.PRIMARY;

  /**
   * The list of targets to deploy to.
   */
  readonly awsDeploymentTargets: Array<AwsDeploymentTarget>;

  /**
   * Hold the deploy workflow so we can add to it in preSynth
   */
  public buildWorkflow: BuildWorkflow;

  /**
   * Was this workflow created externally?
   */
  public externalWorkflow: boolean;

  /**
   * Projects that should complete deployment before this one starts.
   */
  public deployAfterTargets: Array<AwsDeploymentTarget>;

  /**
   * Resolved `cdk diff` capture settings. Every field is concrete so callers
   * never have to re-apply the defaults.
   */
  public readonly diffOptions: Required<DeployDiffOptions>;

  /**
   * Whether each deploy and diff job's concurrency group carries the branch.
   */
  public readonly branchScopedConcurrency: boolean;

  /**
   * Repository this workflow's cloud-touching jobs are pinned to, if any.
   */
  public readonly homeRepository?: string;

  /**
   * GitHub environment this workflow's gated jobs are held behind, if any.
   */
  public readonly environmentName?: string;

  /**
   * Gate model this workflow was built with, or `undefined` when ungated.
   */
  public readonly gate?: DeployGate;

  /**
   * Resolved approval model. Concrete under every gate, and simply unused
   * outside `environment`.
   */
  public readonly approvals: DeployApprovals;

  /**
   * GitHub environment the single `approve` job sits behind under
   * `approvals: "once"`, or `undefined` under `per-target`.
   */
  public readonly approvalEnvironmentName?: string;

  /**
   * Resolved `plan-apply` settings. Every field is concrete so callers never
   * have to re-apply the defaults. Populated under every gate, and simply
   * unused outside `plan-apply`.
   *
   * `applyWorkflow` is omitted because it is an input, not a setting — read
   * {@link applyWorkflow} for the workflow this component actually uses,
   * whether it created or joined it.
   */
  public readonly planApplyOptions: Required<
    Omit<PlanApplyOptions, "applyWorkflow">
  >;

  /**
   * The dispatch-only apply workflow, when the gate is `plan-apply`. Shared
   * with any other `AwsDeployWorkflow` that was pointed at it via
   * `planApply.applyWorkflow`.
   */
  public readonly applyWorkflow?: GithubWorkflow;

  constructor(
    public project: AwsCdkTypeScriptApp,
    public options: DeployWorkflowOptions = {},
  ) {
    super(project);

    /***************************************************************************
     *
     * Root project check
     *
     * Detect the root project and ensure it's of type MonorepoProject.
     *
     **************************************************************************/

    if (!(project.root instanceof MonorepoProject)) {
      throw new Error(
        "AwsDeployWorkflow requires the root project to be a MonorepoProject",
      );
    }

    this.rootProject = project.root;

    /***************************************************************************
     *
     * GitHub Check
     *
     * Make sure github config is active in the project. This is to ensure all
     * workflows will be output properly during synth.
     *
     **************************************************************************/

    const github = GitHub.of(this.rootProject);

    if (!github) {
      throw new Error(
        "AwsDeployWorkflow requires a GitHub component in the root project",
      );
    }

    /***************************************************************************
     *
     * TurboRepo Check
     *
     * If turbo is enabled, we may need the options later in this file.
     *
     **************************************************************************/

    const turbo = TurboRepo.of(this.rootProject);
    const buildWorkflowOptions: Partial<BuildWorkflowOptions> =
      turbo?.remoteCacheOptions
        ? TurboRepo.buildWorkflowOptions(turbo.remoteCacheOptions)
        : {};

    /***************************************************************************
     *
     * Workflow Deploy Type
     *
     * What type of environments are we deploying into? We'll default to dev to
     * be safe.
     *
     **************************************************************************/

    this.awsStageType = options.awsStageType ?? AWS_STAGE_TYPE.DEV;

    /***************************************************************************
     *
     * Workflow Deploy Targets
     *
     * Use provided targets or discover them based on the deploy type. Only
     * include targets that are marked for CI/CD.
     *
     **************************************************************************/

    this.awsDeploymentTargets =
      options.awsDeploymentTargets ??
      AwsDeploymentConfig.of(project)?.awsDeploymentTargets.filter(
        (target) =>
          target.awsStageType === this.awsStageType && target.ciDeployment,
      ) ??
      [];

    this.deployAfterTargets = options.deployAfterTargets ?? [];

    /***************************************************************************
     *
     * Gate Model
     *
     * Resolved before the diff block because `plan-apply` moves the default
     * for `diff.enabled` — a plan whose whole job is to be reviewed is not
     * worth much without a diff to review. With `gate` unset the workflow is
     * ungated and everything below is a no-op.
     *
     **************************************************************************/

    this.gate = options.gate;

    const isPlanApply = this.gate === DEPLOY_GATE.PLAN_APPLY;

    /***************************************************************************
     *
     * Diff Capture
     *
     * Opt-in when ungated or environment-gated. With `diff` unset the resolved
     * `enabled` stays false, no diff jobs are registered, and the generated
     * workflow is byte-identical to one configured without a `diff` block at
     * all.
     *
     * Under `plan-apply` the default flips to true, and turning it off is
     * rejected outright: projen only uploads the build artifact when the
     * workflow has at least one post-build job, and under `plan-apply` the
     * diff jobs are the only post-build jobs left once the deploy jobs move
     * out. A diff-less plan would publish nothing for the apply to download
     * and fail at dispatch time instead of at synth time.
     *
     **************************************************************************/

    if (isPlanApply && options.diff?.enabled === false) {
      throw new Error(
        "AwsDeployWorkflow cannot disable `diff` under the `plan-apply` gate: the diff jobs are what keep the plan workflow publishing the build artifact the apply workflow deploys.",
      );
    }

    const diffEnabled = options.diff?.enabled ?? isPlanApply;

    /**
     * Opt-in, and only meaningful under the `environment` gate — where the diff
     * jobs are the last cloud-touching jobs still authenticating on the ref
     * subject claim, so a deployer role narrowed to environment subjects turns
     * them all away. Validated before the environment itself resolves, so a
     * flag with nothing to borrow fails synth rather than rendering diff jobs
     * that look environment-scoped and are not.
     */
    this.diffOptions = {
      enabled: diffEnabled,
      artifact: options.diff?.artifact ?? true,
      summary: options.diff?.summary ?? true,
      environment: resolveDiffEnvironment(
        options.gate,
        options.diff?.environment,
        diffEnabled,
        "AwsDeployWorkflow",
      ),
    };

    /***************************************************************************
     *
     * Branch-Scoped Concurrency
     *
     * Opt-in. Widens each deploy and diff job's concurrency group with the
     * branch so branch-scoped stacks stop queueing behind each other. Off by
     * default, in which case the group is the target coordinates alone and the
     * rendered deploy job is unchanged.
     *
     **************************************************************************/

    this.branchScopedConcurrency = options.branchScopedConcurrency ?? false;

    /***************************************************************************
     *
     * Environment Gate
     *
     * Opt-in. Puts a GitHub environment on every deploy job so its protection
     * rules run before the job reaches a runner, and — when diff capture is on —
     * orders each deploy behind its own target's diff so the approver reads a
     * finished diff. Resolved before any workflow is constructed so a
     * half-configured gate fails synth early. With `gate` unset no environment
     * is emitted and job ordering is unchanged.
     *
     * Under `plan-apply` the deploy jobs live in the apply workflow, so an
     * environment supplied alongside it lands on the apply jobs instead —
     * a second, differently-authorized approver on top of whoever dispatched
     * the apply. It is optional there rather than required, because the
     * dispatch split is already the gate.
     *
     **************************************************************************/

    this.environmentName = resolveEnvironmentGate(
      options.gate,
      options.environmentName,
      "AwsDeployWorkflow",
    );

    /***************************************************************************
     *
     * Approval Placement
     *
     * Opt-in, and only meaningful under the `environment` gate. `per-target`
     * (the default) leaves the gate as it was: the environment is the whole
     * gate, so every deploy job carries it and every deploy job prompts. Deploy
     * jobs chained by `deployAfterTargets` reach that prompt one at a time —
     * each only after its predecessor has finished deploying — so the prompts
     * arrive as a trickle no GitHub setting can batch.
     *
     * `once` splits the two jobs the environment was doing at the same time. A
     * dedicated `approve` job carries the required-reviewer rule and every
     * deploy job waits on it; the deploy jobs keep their own `environment`, so
     * their OIDC subject claims stay in the `environment:<name>` form,
     * environment-scoped secrets still resolve, and each stack still records
     * its own deployment. Moving the environment onto the gate job instead
     * would have cost all three.
     *
     * Resolved here — after `environmentName`, whose resolved value the
     * same-name check reads — so a half-configured approval gate fails synth
     * before any workflow is constructed.
     *
     **************************************************************************/

    this.approvals = options.approvals ?? DEPLOY_APPROVALS.PER_TARGET;

    this.approvalEnvironmentName = resolveApprovalGate(
      options.gate,
      options.approvals,
      options.approvalEnvironmentName,
      this.environmentName,
      "AwsDeployWorkflow",
    );

    /***************************************************************************
     *
     * Home Repository Guard
     *
     * Opt-in. Pins every job that touches cloud resources to one repository so
     * a mirror or clone of the consumer repo skips them rather than failing at
     * the OIDC credentials step. Resolved before any workflow is constructed so
     * a malformed slug fails synth early. With `homeRepository` unset no guard
     * is emitted and the generated workflow is unchanged.
     *
     **************************************************************************/

    this.homeRepository = options.homeRepository;

    const homeRepositoryCondition = renderHomeRepositoryCondition(
      this.homeRepository,
      "AwsDeployWorkflow",
    );

    /***************************************************************************
     *
     * Build Workflow Options
     *
     * Can't use the options if an existing workflow was passed in.
     *
     **************************************************************************/

    if (options.buildWorkflow && options.buildWorkflowOptions) {
      throw new Error(
        "Cannot provide both buildWorkflow and buildWorkflowOptions",
      );
    }

    this.externalWorkflow = !!options.buildWorkflow;

    /***************************************************************************
     *
     * Build Workflow
     *
     * Create a workflow either based on input or from scratch.
     *
     **************************************************************************/

    this.buildWorkflow =
      options.buildWorkflow ??
      new BuildWorkflow(this.rootProject, {
        /**
         * Name based on project and environment.
         */
        name:
          options.buildWorkflowOptions?.name ??
          [
            "deploy",
            project.name,
            this.awsStageType,
            this.awsEnvironmentType,
          ].join("-"),

        /**
         * Use the root projects build task.
         */
        buildTask: this.rootProject.buildTask,

        /**
         * Use push triggers based n the branch config for each environment.
         *
         * Deduped: targets in one workflow routinely share a branch — two prod
         * targets both default to `main` — and a repeated entry is only noise
         * in the generated file, since GitHub treats this as a filter list.
         */
        workflowTriggers: {
          push: {
            branches: Array.from(
              new Set(
                this.awsDeploymentTargets.flatMap((t) =>
                  t.branches.map((b) => b.branch),
                ),
              ),
            ),
          },
          workflowDispatch: {},
          ...options.buildWorkflowOptions?.workflowTriggers,
        },

        /**
         * Never allow mutations for deploys. This should have been handled
         * during build.
         */
        mutableBuild: false,

        /**
         * Do this pre-merge of permissions and build steps
         */
        ...options.buildWorkflowOptions,

        /**
         * Set GIT_BRANCH_NAME so Turborepo remote cache hashes match between local and CI.
         */
        env: {
          GIT_BRANCH_NAME: BRANCH_NAME_EXPRESSION,
          ...options.buildWorkflowOptions?.env,
          ...buildWorkflowOptions?.env,
        },

        /**
         * Some additional permissions may be required when turbo's involved.
         */
        permissions: {
          ...options.buildWorkflowOptions?.permissions,
          ...buildWorkflowOptions?.permissions,
        },

        /**
         * Assemble all pre-build steps
         */
        preBuildSteps: [
          ...this.setupPnpm(),
          ...this.setupNode(),
          {
            name: "Install dependencies",
            run: "pnpm i --no-frozen-lockfile",
          },
          ...(options.buildWorkflowOptions?.preBuildSteps ?? []),
          ...(buildWorkflowOptions?.preBuildSteps ?? []),
        ],
      });

    /***************************************************************************
     *
     * Home Repository Agreement
     *
     * The pin belongs to the workflow file, not to this component: it guards
     * every cloud-touching job in the file. Recorded against the build workflow
     * here, and against the apply workflow below, so a second component sharing
     * either one cannot stamp a different pin on its own jobs.
     *
     **************************************************************************/

    WorkflowHomeRepository.require(this.rootProject, {
      workflow: this.buildWorkflow,
      workflowName: this.buildWorkflow.name,
      homeRepository: this.homeRepository,
      componentName: "AwsDeployWorkflow",
    });

    /***************************************************************************
     *
     * Apply Workflow
     *
     * Under `plan-apply` the workflow built above becomes the plan: it keeps
     * its triggers, its build job, and its diff jobs, but its deploy jobs move
     * to a second, dispatch-only workflow that runs them against the assembly
     * a nominated plan run already produced.
     *
     * That second workflow is created here, or joined when the supplied
     * `applyWorkflow` points at one another component already created. Joining
     * is what lets a multi-service repo keep its cross-service
     * `deployAfterTargets` ordering, since GitHub resolves `needs` only within
     * one workflow file.
     *
     **************************************************************************/

    const sharedApplyWorkflow = options.planApply?.applyWorkflow;

    if (sharedApplyWorkflow && options.planApply?.applyWorkflowName) {
      throw new Error(
        "Cannot provide both planApply.applyWorkflow and planApply.applyWorkflowName: the workflow being joined already has a name.",
      );
    }

    this.planApplyOptions = {
      applyWorkflowName:
        sharedApplyWorkflow?.name ??
        options.planApply?.applyWorkflowName ??
        `${this.buildWorkflow.name}-apply`,
      requireCurrentHead: options.planApply?.requireCurrentHead ?? false,
      verifyArtifactDigest: options.planApply?.verifyArtifactDigest ?? false,
    };

    if (isPlanApply) {
      const applyWorkflow = sharedApplyWorkflow
        ? this.attachApplyWorkflow(sharedApplyWorkflow)
        : new ApplyWorkflow(this.rootProject, github, {
            applyWorkflowName: this.planApplyOptions.applyWorkflowName,
            planWorkflowName: this.buildWorkflow.name,
            requireCurrentHead: this.planApplyOptions.requireCurrentHead,
            verifyArtifactDigest: this.planApplyOptions.verifyArtifactDigest,
            homeRepositoryCondition,
            awsDeploymentTargets: this.awsDeploymentTargets,
            componentName: "AwsDeployWorkflow",
          });

      this.applyWorkflow = applyWorkflow.workflow;

      WorkflowHomeRepository.require(this.rootProject, {
        workflow: applyWorkflow.workflow,
        workflowName: this.planApplyOptions.applyWorkflowName,
        homeRepository: this.homeRepository,
        componentName: "AwsDeployWorkflow",
      });
    }

    /***************************************************************************
     *
     * Add Deployments to workflow
     *
     **************************************************************************/

    this.awsDeploymentTargets.forEach((target) => {
      const deployJobName = this.buildJobName(target);

      // Build the branch filter condition
      const branchFilterCondition = this.buildBranchFilterCondition(
        target.branches,
      );

      /**
       * Compose the job gate. The build's self-mutation check always applies;
       * the optional home-repository pin and the target's branch filter are
       * ANDed in when present. With both unset the rendered expression is
       * identical to what this workflow emitted before either option existed.
       */
      const jobCondition =
        "${{ " +
        [
          "!needs.build.outputs.self_mutation_happened",
          ...(homeRepositoryCondition ? [homeRepositoryCondition] : []),
          ...(branchFilterCondition ? [`(${branchFilterCondition})`] : []),
        ].join(" && ") +
        " }}";

      /**
       * Under every gate but `plan-apply` the deploy job hangs off the build
       * job in this same workflow. Under `plan-apply` it moves to the apply
       * workflow, where a nominated plan run feeds it instead.
       */
      if (this.applyWorkflow) {
        this.addApplyJob(this.applyWorkflow, target, homeRepositoryCondition);
      } else {
        /**
         * An environment-gated deploy waits on the diff *report*, not on its
         * own target's diff job. The report transitively needs every diff, so
         * the deploy sits in "Waiting" only once the whole picture is on the
         * run page — an approver decides on all of it rather than on one
         * service's slice.
         *
         * Ungated, or with diff capture off, `needs` is unchanged.
         *
         * Under `approvals: "once"` the deploy also waits on the single
         * `approve` job. Everything else about the job — its own `environment`
         * included — is untouched: the approval job holds the reviewer rule,
         * the deploy job's environment keeps the secrets, the OIDC subject
         * claim, and the deployment record.
         */
        const gatedOnDiff = !!this.environmentName && this.diffOptions.enabled;

        this.buildWorkflow.addPostBuildJob(deployJobName, {
          name: `Deploy ${this.project.name} ${target.awsStageType}/${target.deploymentTargetRole}/${target.account}/${target.region}`,
          needs: [
            "build",
            ...(gatedOnDiff ? [DIFF_REPORT_JOB_ID] : []),
            ...(this.approvalEnvironmentName ? [APPROVE_JOB_ID] : []),
            ...this.deployAfterTargets.map((p) => {
              return this.buildJobName(p);
            }),
          ],
          runsOn: ["ubuntu-latest"],
          permissions: {
            contents: JobPermission.READ,
            idToken: JobPermission.WRITE,
            pullRequests: JobPermission.WRITE,
          },
          ...(this.environmentName
            ? { environment: this.environmentName }
            : undefined),
          concurrency: this.buildDeployConcurrency(deployJobName),
          if: jobCondition,
          steps: [...this.deploySteps(target)],
        });
      }

      /**
       * Optional `cdk diff` job for the same target.
       *
       * Registered here — inside the target loop and therefore before
       * `addBuildCompleteJob` runs below — so the aggregate gate job picks the
       * diff jobs up without a second pass.
       *
       * Deliberately narrower than the deploy job:
       *
       * - `needs: [build]` only. A diff is a read-only comparison against live
       *   cloud state, so it does not inherit `deployAfterTargets` ordering and
       *   every target's diff runs in parallel.
       * - Its own `concurrency` group with a `diff` verb, not the deploy job's.
       *   A diff is read-only with respect to stack *state*, but
       *   `--method=change-set` creates a fixed-name change set per stack, so
       *   two runs diffing one target race for that single named slot and the
       *   loser fails. A separate group serializes diff against diff — including
       *   across two workflows carrying the same target, since GitHub scopes
       *   groups to the repository — without also queueing every diff behind a
       *   long deploy the way sharing the deploy group would.
       * - No `pull-requests: write`. The diff job posts no PR comment, and
       *   `actions/upload-artifact` authenticates with the Actions runtime
       *   token rather than `GITHUB_TOKEN`, so it needs no extra permission.
       * - No `environment` by default, even when the deploy jobs are gated. The
       *   gate exists so an approver can read the diff, and a diff held behind
       *   a required-reviewer rule would wait for the approval it informs.
       *
       *   `diff.environment` opts back in, for the one thing the environment
       *   does to a job besides gate it: a job that declares one requests an
       *   OIDC token subject-claimed `environment:<name>` rather than
       *   `ref:refs/heads/<branch>`. Under the `environment` gate these are the
       *   only cloud-touching jobs left on the ref form, so a deployer role
       *   trusting environment subjects alone rejects every diff — and the
       *   merged report gates the deploys, so the run dies before deploying.
       *   Whether the environment *also* pauses the job is a property of its
       *   protection rules, which is why the flag pairs with an approval rule
       *   kept on a separate `approvalEnvironmentName` rather than on this one.
       *
       * It renders no summary of its own — its capture is uploaded as a part
       * for the report job, which is the single place the diffs are read.
       */
      if (this.diffOptions.enabled) {
        const diffJobName = this.buildDiffJobName(target);

        this.buildWorkflow.addPostBuildJob(diffJobName, {
          name: `Diff ${this.project.name} ${target.awsStageType}/${target.deploymentTargetRole}/${target.account}/${target.region}`,
          needs: ["build"],
          runsOn: ["ubuntu-latest"],
          permissions: {
            contents: JobPermission.READ,
            idToken: JobPermission.WRITE,
          },
          ...(this.diffOptions.environment && this.environmentName
            ? { environment: this.environmentName }
            : undefined),
          concurrency: this.buildDiffConcurrency(diffJobName),
          if: jobCondition,
          steps: [...this.diffSteps(target)],
        });
      }
    });

    /***************************************************************************
     *
     * Diff Report
     *
     * The diffs themselves stay in their own parallel jobs — each target owns
     * its own stacks and needs its own account's credentials, so only two diffs
     * of one target ever serialize. What does not scale is the review: under
     * `plan-apply` the diff is the *only* review surface, so a multi-service
     * repo would mean one job summary per service to open and reconcile by
     * hand. Every target uploads its capture as a part, and one report job
     * merges them into a single summary and a single artifact.
     *
     * Registered before `addBuildCompleteJob` below so the aggregate gate job
     * picks it up, and after the target loop so its `needs` name jobs that
     * already exist.
     *
     **************************************************************************/

    if (this.diffOptions.enabled && this.awsDeploymentTargets.length > 0) {
      DiffReportJob.attach(this.rootProject, {
        buildWorkflow: this.buildWorkflow,
        homeRepositoryCondition,
        summary: this.diffOptions.summary,
        artifact: this.diffOptions.artifact,
        targets: this.awsDeploymentTargets.map((target) => ({
          label: `${this.project.name} ${target.awsStageType}/${target.deploymentTargetRole}/${target.account}/${target.region}`,
          jobName: this.buildDiffJobName(target),
          outputFile: this.buildDiffOutputFile(target),
          branchCondition: this.buildBranchFilterCondition(target.branches),
        })),
      });
    }

    /***************************************************************************
     *
     * Single Approval Job
     *
     * The one job under `approvals: "once"` that actually pauses. It needs the
     * diff report — the same thing a `per-target` deploy waits on — so the
     * reviewer still reads the combined `cdk diff` for every target before
     * anything deploys, and with diff capture off it falls back to `build`
     * alone exactly as a gated deploy does.
     *
     * Registered on the workflow directly rather than through
     * `addPostBuildJob`, which would prepend a download of the build artifact
     * this job has no use for — it runs no CDK and touches no cloud. The deploy
     * jobs remain post-build jobs, so the invariant that keeps projen uploading
     * the build artifact is unaffected.
     *
     * Registered after the diff report and before `addBuildCompleteJob`, which
     * enumerates the workflow's jobs to build the aggregate gate's `needs` — a
     * job added later would be missing from that gate.
     *
     **************************************************************************/

    if (this.approvalEnvironmentName && this.awsDeploymentTargets.length > 0) {
      if (this.buildWorkflow.workflow.getJob(APPROVE_JOB_ID)) {
        throw new Error(
          `AwsDeployWorkflow cannot add a second \`approvals: "${DEPLOY_APPROVALS.ONCE}"\` gate to the workflow "${this.buildWorkflow.name}": the job id "${APPROVE_JOB_ID}" is already taken. One approval job releases every deploy job in the file, so give the components separate build workflows if they must be approved separately.`,
        );
      }

      const gatedOnDiff = this.diffOptions.enabled;

      this.buildWorkflow.workflow.addJob(APPROVE_JOB_ID, {
        name: `Approve ${this.project.name} ${this.awsStageType} deployment`,
        needs: ["build", ...(gatedOnDiff ? [DIFF_REPORT_JOB_ID] : [])],
        runsOn: ["ubuntu-latest"],
        /**
         * Nothing to read and nothing to write: the job exists so GitHub holds
         * it at the environment's protection rules, and its only step records
         * that it cleared them.
         */
        permissions: {
          contents: JobPermission.NONE,
        },
        environment: this.approvalEnvironmentName,
        if: this.buildApprovalCondition(homeRepositoryCondition),
        steps: [
          {
            name: "Record approval",
            run: `echo "Approved — releasing every ${this.awsStageType} deploy job in this run." >> "$GITHUB_STEP_SUMMARY"`,
          },
        ],
      });
    }

    /**
     * Append the "complete" gate job (ADR 0004) so this workflow has a single
     * required status check.
     */
    addBuildCompleteJob(this.buildWorkflow);
  }

  public setupNode = (): Array<JobStep> => renderSetupNode();

  public setupPnpm = (): Array<JobStep> =>
    renderSetupPnpm(this.rootProject.pnpmVersion);

  /**
   * Build the deterministic GitHub Actions job name for a deploy target.
   *
   * Used both to register the post-build job and as the sticky-PR-comment
   * header so dev/stage/prod comments don't collide on the same PR.
   */
  private buildJobName = (target: AwsDeploymentTarget): string => {
    return [
      target.awsStageType,
      target.deploymentTargetRole,
      "deploy",
      target.project.name,
      target.account,
      target.region,
    ].join("-");
  };

  /**
   * Build a deploy job's `concurrency` setting from its job name.
   *
   * Two shapes, deliberately:
   *
   * - Unscoped (the default) stays the bare string the job has always carried,
   *   so an existing consumer's workflow is byte-identical.
   * - Branch-scoped spells out `cancel-in-progress: false` rather than leaning
   *   on GitHub's default, the same way the apply job does. A group keyed on
   *   the branch reads like a "cancel superseded pushes" group, and that is the
   *   one thing it must not be — cancelling a half-finished `cdk deploy`
   *   strands a CloudFormation stack mid-update.
   */
  private buildDeployConcurrency = (
    deployJobName: string,
  ): string | { group: string; "cancel-in-progress": boolean } => {
    if (!this.branchScopedConcurrency) {
      return deployJobName;
    }

    return {
      group: `${deployJobName}-${BRANCH_NAME_EXPRESSION}`,
      "cancel-in-progress": false,
    };
  };

  /**
   * Build the deterministic GitHub Actions job name for a target's apply job.
   * Mirrors {@link buildJobName} with an `apply` verb.
   */
  private buildApplyJobName = (target: AwsDeploymentTarget): string => {
    return [
      target.awsStageType,
      target.deploymentTargetRole,
      "apply",
      target.project.name,
      target.account,
      target.region,
    ].join("-");
  };

  /**
   * Build the deterministic GitHub Actions job name for a target's `cdk diff`
   * job. Mirrors {@link buildJobName} with a `diff` verb so the diff and deploy
   * jobs for one target sort next to each other in the run view.
   */
  private buildDiffJobName = (target: AwsDeploymentTarget): string => {
    return [
      target.awsStageType,
      target.deploymentTargetRole,
      "diff",
      target.project.name,
      target.account,
      target.region,
    ].join("-");
  };

  /**
   * Build a diff job's `concurrency` setting from its job name.
   *
   * Always the object form, unlike {@link buildDeployConcurrency}: a diff job
   * has no legacy bare-string shape to stay byte-identical with, and
   * `cancel-in-progress` is load-bearing enough here to spell out. It must
   * stay `false` — a superseded diff cancelled mid-`CreateChangeSet`
   * leaves the fixed-name change set occupied for whichever run comes next,
   * which is the failure the group exists to prevent.
   *
   * Carries the branch under
   * {@link DeployWorkflowOptions.branchScopedConcurrency} for the same reason
   * the deploy group does: when stack names are derived from the branch, two
   * branches diff separate stacks and therefore separate change sets, so one
   * repo-wide lock across all of them would queue for nothing.
   */
  private buildDiffConcurrency = (
    diffJobName: string,
  ): { group: string; "cancel-in-progress": boolean } => {
    return {
      group: this.branchScopedConcurrency
        ? `${diffJobName}-${BRANCH_NAME_EXPRESSION}`
        : diffJobName,
      "cancel-in-progress": false,
    };
  };

  /**
   * Build the slug that makes a target unique among every target in the plan
   * workflow, across every deploy component feeding it.
   *
   * Includes `deploymentTargetRole`, which two otherwise-identical targets can
   * differ on.
   */
  private buildDiffSlug = (target: AwsDeploymentTarget): string => {
    return [
      target.awsStageType,
      target.deploymentTargetRole,
      target.project.name,
      target.account,
      target.region,
    ].join("-");
  };

  /**
   * Build the file a target's captured diff is written to, both in its own diff
   * job's workspace and in the merged directory the report job assembles.
   */
  private buildDiffOutputFile = (target: AwsDeploymentTarget): string => {
    return `${DIFF_OUTPUT_DIRECTORY}/${this.buildDiffSlug(target)}.txt`;
  };

  /**
   * Build the intermediate artifact name carrying one target's captured diff.
   *
   * The diff jobs run in parallel and `actions/upload-artifact` v4+ rejects a
   * duplicate name within a run, so each target needs its own. The report job
   * merges them into a single artifact, which is the one meant to be read.
   */
  private buildDiffPartArtifactName = (target: AwsDeploymentTarget): string => {
    return `${DIFF_PART_ARTIFACT_PREFIX}-${this.buildDiffSlug(target)}`;
  };

  /**
   * Join an apply workflow another `AwsDeployWorkflow` already created, so this
   * component's apply jobs land in that file rather than a second one.
   *
   * Rejects a `GithubWorkflow` that is not an apply workflow: attaching to an
   * arbitrary one would add apply jobs depending on a `validate-plan` job that
   * does not exist there, which GitHub rejects only at run time.
   */
  private attachApplyWorkflow = (workflow: GithubWorkflow): ApplyWorkflow => {
    const shared = ApplyWorkflow.of(this.rootProject, workflow);

    if (!shared) {
      throw new Error(
        `AwsDeployWorkflow cannot attach to the workflow "${workflow.name}" because it is not an apply workflow. Pass the \`applyWorkflow\` of an AwsDeployWorkflow built with the \`plan-apply\` gate.`,
      );
    }

    shared.attach({
      planWorkflowName: this.buildWorkflow.name,
      requireCurrentHead: this.planApplyOptions.requireCurrentHead,
      verifyArtifactDigest: this.planApplyOptions.verifyArtifactDigest,
      awsDeploymentTargets: this.awsDeploymentTargets,
      componentName: "AwsDeployWorkflow",
    });

    return shared;
  };

  /**
   * Register one target's apply job on the apply workflow.
   *
   * Differences from the `single`-gate deploy job, all of them forced by the
   * apply run having no build job of its own:
   *
   * - `needs` starts at the validation job rather than `build`, so a plan run
   *   that fails any check skips every apply job instead of deploying.
   * - `actions: read` is added, which is what lets `actions/download-artifact`
   *   reach across runs.
   * - No `pull-requests: write`, and no sticky PR comment step: an apply is
   *   always a `workflow_dispatch`, so the comment could never fire.
   * - No branch filter on `github.ref`. The ref an operator happens to
   *   dispatch from is unrelated to what was planned; the branch that matters
   *   is the plan run's, and the validation job checks that one.
   *
   * When `environmentName` is also supplied, it lands here rather than on the
   * plan's jobs — a second approver on top of whoever dispatched the apply.
   * `validate-plan` stays ungated so its verdict is available to read *before*
   * the approval is given.
   */
  private addApplyJob = (
    workflow: GithubWorkflow,
    target: AwsDeploymentTarget,
    homeRepositoryCondition: string | undefined,
  ): void => {
    const applyJobName = this.buildApplyJobName(target);
    const { verifyArtifactDigest } = this.planApplyOptions;
    const artifactsDirectory = this.rootProject.artifactsDirectory;

    workflow.addJob(applyJobName, {
      name: `Apply ${this.project.name} ${target.awsStageType}/${target.deploymentTargetRole}/${target.account}/${target.region}`,
      needs: [
        VALIDATE_PLAN_JOB_ID,
        ...this.deployAfterTargets.map((p) => this.buildApplyJobName(p)),
      ],
      runsOn: ["ubuntu-latest"],
      permissions: {
        actions: JobPermission.READ,
        contents: JobPermission.READ,
        idToken: JobPermission.WRITE,
      },
      ...(this.environmentName
        ? { environment: this.environmentName }
        : undefined),
      /**
       * Shares the deploy job's group name so an apply can never overlap a
       * same-target deploy. `cancel-in-progress` is spelled out rather than
       * left to GitHub's default: cancelling a half-finished `cdk deploy`
       * strands a CloudFormation stack mid-update.
       *
       * `branchScopedConcurrency` deliberately does not reach here. An apply is
       * dispatched from whatever ref the operator happens to be on — unrelated
       * to the branch the nominated plan ran against, which is the only branch
       * that describes what is about to be deployed. Keying on the dispatch ref
       * would hand out separate locks to applies of the same target, so the
       * unsuffixed group stays.
       */
      concurrency: {
        group: this.buildJobName(target),
        "cancel-in-progress": false,
      },
      ...(homeRepositoryCondition
        ? { if: `\${{ ${homeRepositoryCondition} }}` }
        : {}),
      steps: [
        /**
         * Cross-run download of the plan's assembly. `run-id` plus an explicit
         * `github-token` is what makes `actions/download-artifact` look outside
         * the current run; without both it silently searches this run and finds
         * nothing.
         */
        {
          name: "Download plan build artifacts",
          uses: "actions/download-artifact@v8",
          with: {
            name: BUILD_ARTIFACT_NAME,
            path: artifactsDirectory,
            "run-id": `\${{ inputs.${PLAN_RUN_ID_INPUT} }}`,
            "github-token": "${{ secrets.GITHUB_TOKEN }}",
            ...(verifyArtifactDigest
              ? {
                  "artifact-digest": `\${{ needs.${VALIDATE_PLAN_JOB_ID}.outputs.artifact_digest }}`,
                }
              : {}),
          },
        },
        {
          name: "Restore build artifact permissions",
          continueOnError: true,
          run: `cd ${artifactsDirectory} && setfacl --restore=${PERMISSION_BACKUP_FILE}`,
        },
        ...this.deploySteps(target, { stickyPrComment: false }),
      ],
    });
  };

  /**
   * Builds a GitHub Actions condition string that checks if the current branch
   * matches any of the provided branch patterns.
   *
   * Handles both exact matches (e.g., "main") and glob patterns (e.g., "feature/*").
   * Also allows workflow_dispatch (manual runs) to proceed.
   *
   * @param branches - Array of GitBranch objects with branch patterns
   * @returns Condition string or empty string if no branches provided
   */
  private buildBranchFilterCondition = (branches: Array<GitBranch>): string => {
    if (!branches || branches.length === 0) {
      return "";
    }

    const conditions: Array<string> = [];

    // Always allow workflow_dispatch (manual workflow runs)
    conditions.push("github.event_name == 'workflow_dispatch'");

    for (const branch of branches) {
      const branchPattern = branch.branch;

      // Handle glob patterns (e.g., "feature/*")
      if (branchPattern.includes("*")) {
        // Replace * with proper startsWith check
        const prefix = branchPattern.replace(/\*.*$/, "");
        conditions.push(`startsWith(github.ref, 'refs/heads/${prefix}')`);
        // PR support, unprefixed branch names
        conditions.push(`startsWith(github.head_ref, '${prefix}')`);
      } else {
        // Exact match
        conditions.push(`github.ref == 'refs/heads/${branchPattern}'`);
      }
    }

    /**
     * Combine all conditions with OR
     */
    return conditions.join(" || ");
  };

  /**
   * Compose the single approval job's condition.
   *
   * Mirrors a deploy job's, with one difference forced by the job being shared:
   * the branch clause is the union of every target's, rather than one target's.
   * Without it a push to a branch no target deploys from would still pause for
   * an approval — prompting for a deployment that can never happen, and holding
   * the `complete` gate pending while it waits.
   *
   * A target with no branch filter deploys from every branch, so one of those
   * collapses the union to no clause at all.
   *
   * Deliberately no `!cancelled()`, unlike the diff report: a report is wanted
   * precisely when a diff failed, but an approval is not. GitHub's default —
   * skip a job whose `needs` failed — is what should happen here, and it
   * cascades to every deploy job waiting on the approval.
   */
  private buildApprovalCondition = (
    homeRepositoryCondition: string | undefined,
  ): string => {
    const branchConditions = this.awsDeploymentTargets.map((target) =>
      this.buildBranchFilterCondition(target.branches),
    );
    const anyBranch = branchConditions.some((condition) => !condition);
    const branchClause = anyBranch
      ? undefined
      : Array.from(new Set(branchConditions)).join(" || ");

    return (
      "${{ " +
      [
        "!needs.build.outputs.self_mutation_happened",
        ...(homeRepositoryCondition ? [homeRepositoryCondition] : []),
        ...(branchClause ? [`(${branchClause})`] : []),
      ].join(" && ") +
      " }}"
    );
  };

  /**
   * Steps for a target's deploy job.
   *
   * `stickyPrComment` drops the sticky-PR-comment step when false. Apply jobs
   * pass false: they only ever run on `workflow_dispatch` and so could never
   * satisfy that step's own `pull_request` guard.
   */
  private deploySteps = (
    target: AwsDeploymentTarget,
    options: { stickyPrComment?: boolean } = {},
  ): Array<JobStep> => {
    const { stickyPrComment = true } = options;
    const {
      awsStageType,
      deploymentTargetRole,
      account,
      region,
      ciDeploymentConfig,
      awsDeploymentConfig,
    } = target;
    const { roleArn, stackPattern } = ciDeploymentConfig ?? {};
    const { rootCdkOut, cdkCli } = awsDeploymentConfig;
    const deployJobName = this.buildJobName(target);

    return [
      ...this.setupPnpm(),
      ...this.setupNode(),

      /**
       * Configure AWS creds.
       */
      {
        name: `AWS Creds ${awsStageType}/${deploymentTargetRole}/${account}/${region}`,
        uses: "aws-actions/configure-aws-credentials@v6",
        with: {
          "role-to-assume": roleArn,
          "aws-region": region,
          "role-duration-seconds": 900, // 15 minutes
        },
      },

      /**
       * Run CDK Deploy
       */
      {
        name: `Deploy ${awsStageType}/${deploymentTargetRole}/${account}/${region}`,
        run: `pnpm dlx "aws-cdk@${cdkCli.cliVersion}" ${renderCdkDeploy({
          ...cdkCli.deployOptionsFor(target),
          app: rootCdkOut,
          stackPatterns: stackPattern ? [stackPattern] : undefined,
        })} --outputs-file cdk-outputs.json`,
      },

      /**
       * Render a job-summary table listing every `*Endpoint` output emitted
       * by the deploy. Also writes the same list to `deploy-urls.md` so the
       * follow-up sticky-PR-comment step can reuse it.
       *
       * The file is only created when jq returns at least one match — an
       * empty `deploy-urls.md` would pass the downstream `hashFiles(...)`
       * guard and then fail the sticky-comment action with
       * "Either message or path input is required" (issue #721).
       */
      {
        name: "Render deploy summary",
        if: "hashFiles('cdk-outputs.json') != ''",
        shell: "bash",
        run: [
          '# Convention: CfnOutputs whose logical ID ends in "Endpoint" are surfaced.',
          "# See docs/packages/@codedrifters/configulator/workflows/aws-deploy-workflow#surfacing-deploy-urls",
          `endpoints=$(jq -r '[.[] | to_entries[] | select(.key | test("Endpoint$")) | .value] | .[] | "- [\\(.)](\\(.))"' cdk-outputs.json)`,
          'if [ -n "$endpoints" ]; then',
          '  echo "## Deployed endpoints" >> "$GITHUB_STEP_SUMMARY"',
          '  echo "$endpoints" >> "$GITHUB_STEP_SUMMARY"',
          '  echo "" >> "$GITHUB_STEP_SUMMARY"',
          '  echo "_DNS/CloudFront may take ~1 minute to propagate._" >> "$GITHUB_STEP_SUMMARY"',
          "  printf '%s\\n' \"$endpoints\" > deploy-urls.md",
          "fi",
        ].join("\n"),
      },

      /**
       * Post a sticky PR comment listing each deployed endpoint. The
       * comment is keyed by the deploy job name so dev/stage/prod comments
       * don't collide on the same PR.
       */
      ...(stickyPrComment
        ? [
            {
              name: "Sticky PR comment with deploy endpoints",
              if: "github.event_name == 'pull_request' && hashFiles('deploy-urls.md') != ''",
              uses: "marocchino/sticky-pull-request-comment@v3",
              with: {
                header: deployJobName,
                path: "deploy-urls.md",
              },
            },
          ]
        : []),
    ];
  };

  /**
   * Steps for a target's optional `cdk diff` job.
   *
   * Mirrors {@link deploySteps}' toolchain and credential setup — same pnpm /
   * Node setup, same version-pinned `aws-cdk` CLI, same OIDC role — so a diff
   * is computed by the same CLI that will run the deploy.
   *
   * The `cdk diff` flags come from the {@link CdkCli} precedence chain via
   * `diffOptionsFor(target)`, so `method`, `securityOnly`, `fail`, and the rest
   * are consumer-controlled per stage, per account, or per target. Four options
   * are pinned at the call site because the capture mechanism depends on them:
   * `app` and `stackPatterns` target the built cloud assembly, and `ci` /
   * `noColor` force the human-readable diff onto stdout without ANSI escapes
   * (without `--ci` the CLI interleaves it into stderr alongside logs).
   *
   * `method` is the one resolved flag not baked into the rendered command. A
   * from-scratch target has to be classified before it can be diffed — see
   * {@link diffProbeLines} — so the command reads the effective method from
   * `$method`.
   */
  private diffSteps = (target: AwsDeploymentTarget): Array<JobStep> => {
    const {
      awsStageType,
      deploymentTargetRole,
      account,
      region,
      ciDeploymentConfig,
      awsDeploymentConfig,
    } = target;
    const { roleArn, stackPattern } = ciDeploymentConfig ?? {};
    const { rootCdkOut, cdkCli } = awsDeploymentConfig;
    const label = `${awsStageType}/${deploymentTargetRole}/${account}/${region}`;
    const outputFile = this.buildDiffOutputFile(target);
    const stackPatterns = stackPattern ? [stackPattern] : undefined;
    const diffCommand = [
      `pnpm dlx "aws-cdk@${cdkCli.cliVersion}"`,
      renderCdkDiff({
        ...cdkCli.diffOptionsFor(target),
        app: rootCdkOut,
        ci: true,
        noColor: true,
        method: undefined,
        stackPatterns: undefined,
      }),
      '--method="$method"',
      ...(stackPatterns ?? []).map((pattern) => `"${pattern}"`),
    ].join(" ");

    return [
      ...this.setupPnpm(),
      ...this.setupNode(),

      /**
       * Configure AWS creds.
       */
      {
        name: `AWS Creds ${label}`,
        uses: "aws-actions/configure-aws-credentials@v6",
        with: {
          "role-to-assume": roleArn,
          "aws-region": region,
          "role-duration-seconds": 900, // 15 minutes
        },
      },

      /**
       * Classify the target's stacks, then run CDK Diff, capturing the output
       * while leaving it visible in the job log. `pipefail` keeps the CLI's
       * exit code from being masked by `tee`.
       *
       * The capture lands under the same path the report job will merge it
       * back to, so a target's file name is identical in both places. It is
       * truncated first and appended to from there, so a re-run never reads a
       * previous attempt's banners.
       */
      {
        name: `Diff ${label}`,
        shell: "bash",
        run: [
          "set -o pipefail",
          `mkdir -p ${DIFF_OUTPUT_DIRECTORY}`,
          `: > ${outputFile}`,
          ...this.diffProbeLines(target, outputFile),
          `${diffCommand} 2>&1 | tee -a ${outputFile}`,
        ].join("\n"),
      },

      /**
       * Hand the capture to the report job. Guarded on `!cancelled()` rather
       * than success, so a diff that exits non-zero — a synth error, or a
       * consumer who opted into `--fail` — still contributes what it captured
       * instead of leaving a hole in the report.
       */
      renderDiffPartUploadStep({
        label,
        artifactName: this.buildDiffPartArtifactName(target),
        outputFile,
      }),
    ];
  };

  /**
   * Lines that classify every stack a target's pattern matches before the diff
   * runs, so a stack that will be created from scratch is reported as such
   * instead of failing the job.
   *
   * `cdk diff --method=change-set` asks CloudFormation for an `UPDATE` change
   * set whenever the stack exists — and a `ROLLBACK_COMPLETE` /
   * `ROLLBACK_FAILED` tombstone left by a failed first create does exist, so
   * CloudFormation rejects the request and the job goes red. `cdk deploy`
   * consults `stackStatus.isCreationFailure`, deletes the tombstone and
   * recreates the stack, so the apply succeeds on exactly the stack the diff
   * refuses to look at. Under the `plan-apply` gate that asymmetry is a hard
   * blocker rather than a cosmetic one: the apply requires a successful plan
   * run, so one from-scratch target blocks applying any plan for the stage.
   *
   * A genuinely absent stack fails the same way for a different reason:
   * `CreateChangeSet` resolves `AWS::SSM::Parameter::Value` template
   * parameters when the change set is created, so a never-deployed stack that
   * consumes parameters written by other not-yet-deployed stacks cannot get a
   * `CREATE` change set at all (`Unable to fetch parameters [...] from
   * parameter store for this account`). That deadlocks the first deployment of
   * an entire stage: no green plan without the upstream stacks, no upstream
   * stacks without an apply, no apply without a green plan.
   *
   * `cdk list --long --json` resolves the pattern to CloudFormation stack names
   * out of the built assembly's manifest — no synth, no cloud call — and each
   * name costs one `describe-stacks`, which needs only
   * `cloudformation:DescribeStacks`. Any role that can run a change-set diff
   * already carries it. A `cdk list` that fails contributes no names, leaving
   * the diff to run exactly as it does today.
   *
   * Both from-scratch arms therefore move the method, and only to `template`,
   * never to `auto`: `auto` would swallow a real permission failure and
   * present a degraded diff as authoritative, which is what an explicit
   * `change-set` pin exists to prevent. `template` over-reporting replacements
   * is vacuous here, because every resource in a stack built from scratch is
   * an add. A stack in any other status keeps the configured method.
   *
   * The method is per invocation, not per stack — `cdk diff` diffs everything
   * its pattern matches in one pass. A pattern matching several stacks where
   * only one is from-scratch therefore diffs all of them with `template`, and
   * the banner names the stack that caused it.
   */
  private diffProbeLines = (
    target: AwsDeploymentTarget,
    outputFile: string,
  ): Array<string> => {
    const { ciDeploymentConfig, awsDeploymentConfig } = target;
    const { stackPattern } = ciDeploymentConfig ?? {};
    const { rootCdkOut, cdkCli } = awsDeploymentConfig;
    const configuredMethod =
      cdkCli.diffOptionsFor(target).method ?? CDK_DIFF_METHOD.ChangeSet;
    const listCommand = `pnpm dlx "aws-cdk@${cdkCli.cliVersion}" ${renderCdkList(
      {
        app: rootCdkOut,
        json: true,
        long: true,
        stackPatterns: stackPattern ? [stackPattern] : undefined,
      },
    )}`;

    return [
      `method=${configuredMethod}`,
      "while read -r stack; do",
      '  status=$(aws cloudformation describe-stacks --stack-name "$stack" --query "Stacks[0].StackStatus" --output text 2>/dev/null || echo NOT_FOUND)',
      '  case "$status" in',
      "    NOT_FOUND|REVIEW_IN_PROGRESS)",
      `      method=${CDK_DIFF_METHOD.Template}`,
      `      echo "${DIFF_NEW_STACK_MARKER} — $stack does not exist yet, so this target will be created from scratch. Diffing it with --method=${CDK_DIFF_METHOD.Template}." | tee -a ${outputFile}`,
      "      ;;",
      "    ROLLBACK_COMPLETE|ROLLBACK_FAILED)",
      `      method=${CDK_DIFF_METHOD.Template}`,
      `      echo "${DIFF_NEW_STACK_MARKER} — $stack exists only as a failed-create tombstone ($status), so this target will be created from scratch. Diffing it with --method=${CDK_DIFF_METHOD.Template}." | tee -a ${outputFile}`,
      "      ;;",
      "  esac",
      `done < <(${listCommand} 2>/dev/null | jq -r '.[].name' 2>/dev/null)`,
    ];
  };

  preSynthesize(): void {
    /**
     * Ensure turbo is active when needed.
     */
    if (!this.externalWorkflow && TurboRepo.of(this.rootProject)) {
      this.buildWorkflow.addPostBuildSteps(
        {
          name: "Build Sub Projects",
          run: `pnpm exec projen ${ROOT_CI_TASK_NAME}`,
        },
        WorkflowSteps.uploadArtifact({
          name: "Upload Turbo runs",
          if: "always()",
          continueOnError: true,
          with: {
            name: "turbo-runs",
            path: ".turbo/runs",
          },
        }),
      );
    }

    super.preSynthesize();
  }
}
